import {
    VpnKey as KeyIcon,
    Logout as LogoutIcon,
    Menu as MenuIcon,
    MenuOpen as MenuOpenIcon,
    Settings as SettingsIcon,
    Person as UserIcon,
    AccountBalanceWallet as WalletIcon,
    BugReport as BugIcon,
    NewReleases as ChangelogIcon,
    Notifications as NotificationsIcon,
    InfoOutlined as InfoIcon,
    MenuBook as HelpIcon,
} from '@mui/icons-material';
import {
    AppBar,
    Avatar,
    Box,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    Toolbar,
    Tooltip,
    Typography,
    Divider,
    ListItemIcon,
    ListItemText,
    Badge,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCashDrawer } from '../contexts/CashDrawerContext';
import { configurationTabPath } from '../domain/configurationTabs';
import { appRelease } from '../domain/appRelease';
import { useDebugData } from '../contexts/DebugDataContext';
import useQuery, { useDeferredQuery } from '../hooks/useQuery';
import BridgeStatusIndicator from './BridgeStatusIndicator';
import EInvoiceDriftBadge from './eInvoice/EInvoiceDriftBadge';
import HeaderPageSearch from './HeaderPageSearch';

interface HeaderProps {
    collapsed: boolean;
    setCollapsed: (collapsed: boolean) => void;
}

/**
 * Header items shown elsewhere too (the version in the information tooltip, the user name in the
 * user menu, date and time in the taskbar): only on wide windows (MUI `xl`, 1536 px), so the
 * figures that are only here keep their room on a laptop screen.
 */
const WIDE_ONLY = { display: { xs: 'none', xl: 'inline-flex' } };

const Header = ({ collapsed, setCollapsed }: HeaderProps) => {
    const { t } = useTranslation();
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const { balance: cashDrawerBalance, refreshBalance } = useCashDrawer();
    const { setIsDebugWindowOpen, isDebugWindowOpen } = useDebugData();
    const { data: refreshData } = useQuery<{ lastRefresh: string | null }>({
        path: '/contracts/last-refresh',
    });
    // Legacy hides the two aggregate cash figures from non-administrators
    // (`tmain.show_casa`, old/delphi/Umain.pas:5857-5866), so they are not
    // requested either unless the user is allowed to see them.
    const isAdmin =
        user?.role?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'administrator';
    // `/control-room/grand-totals` sums the `finance` asset positions, which is
    // what the Control Room wants but is not register cash. It is still read
    // here for the BNR reference rate, which every role sees.
    const { data: grandTotals } = useQuery<{ lei: number; exchangeRate: number | null }>({
        path: '/control-room/grand-totals',
        pollInterval: 30000,
    });
    const { data: cashStandsTotal, reload: reloadCashStandsTotal } = useDeferredQuery<{
        lei: number;
    }>({
        path: '/control-room/cash-stands-total',
        shouldExecute: isAdmin,
        pollInterval: 30000,
    });
    const { data: cumulativeDifference, reload: reloadCumulativeDifference } = useDeferredQuery<{
        handoverDifference: number;
        registryAdjustment: number;
        total: number;
        since: string;
    }>({
        path: '/cash-drawer/cumulative-difference',
        shouldExecute: isAdmin,
        pollInterval: 30000,
    });
    /**
     * A transaction booked in the legacy application reaches this app through
     * the bridge, and the backend materialises `casa_stands` at the end of that
     * call. Re-reading the three figures as soon as the call settles is what
     * keeps the two headers showing the same numbers side by side; without it
     * they only converge on the next thirty-second poll.
     *
     * The two aggregates are admin-only, so a non-administrator refreshes only
     * the register standing it is allowed to see.
     */
    const handleBridgeCallSettled = useCallback(() => {
        refreshBalance();
        if (isAdmin) {
            reloadCashStandsTotal();
            reloadCumulativeDifference();
        }
    }, [isAdmin, refreshBalance, reloadCashStandsTotal, reloadCumulativeDifference]);
    const { version: appVersion, lastUpdate: lastProgramUpdate } = appRelease;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [currentTime, setCurrentTime] = useState(() => new Date());

    useEffect(() => {
        const interval = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(interval);
    }, []);

    const headerDifference = cumulativeDifference ? Number(cumulativeDifference.total) : null;
    // Money is displayed without decimals per the project display rule. The exact
    // value is kept in the tooltip so a parallel run against the legacy header
    // (which prints the raw float) can still be compared figure for figure.
    const formatAmount = (amount: number) =>
        new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(amount);
    const formatExactAmount = (amount: number) =>
        new Intl.NumberFormat(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    /**
     * The BNR reference rate is stored to four decimals (`contracts.exchange_rate` is
     * `decimal(10,4)`) and is published and quoted that way - by the daily report and by
     * the legacy application alike - so it is shown in full rather than rounded down to
     * the money format the cash amounts use. Trailing zeros are kept: a rate reads as
     * 5.0850, not 5.085.
     */
    const formatExchangeRate = (rate: number) =>
        new Intl.NumberFormat(undefined, {
            minimumFractionDigits: 4,
            maximumFractionDigits: 4,
        }).format(rate);

    const { data: pendingApprovalsData } = useDeferredQuery<{ count: number }>({
        path: '/postponement-approvals/pending-count',
        shouldExecute: isAdmin,
        pollInterval: 60000, // 1 minute
    });

    const pendingApprovalsCount = pendingApprovalsData?.count ?? 0;

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleMenuClick = (action: string) => {
        handleMenuClose();
        switch (action) {
            case 'logout':
                logout();
                break;
            case 'profile':
                navigate(configurationTabPath('profile'));
                break;
            case 'change-password':
                navigate(configurationTabPath('password'));
                break;
            case 'settings':
                navigate(configurationTabPath('preferences'));
                break;
            case 'changelog':
                navigate('/changelog');
                break;
            case 'help':
                navigate('/help');
                break;
            case 'debug':
                setIsDebugWindowOpen(!isDebugWindowOpen);
                break;
            default:
                break;
        }
    };

    return (
        <AppBar
            position="sticky"
            color="default"
            elevation={1}
            sx={{
                // backgroundColor and color inherited from theme
                zIndex: theme => theme.zIndex.drawer + 1,
            }}
        >
            <Toolbar sx={{ minHeight: '44px !important', py: 0.5, gap: 1 }}>
                <Box display="flex" alignItems="center" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={() => setCollapsed(!collapsed)}
                        sx={{ mr: 1 }}
                        size="small"
                    >
                        {collapsed ? (
                            <MenuIcon fontSize="small" />
                        ) : (
                            <MenuOpenIcon fontSize="small" />
                        )}
                    </IconButton>

                    <Typography
                        variant="subtitle1"
                        sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: '0.875rem' }}
                        data-testid="header-app-name"
                    >
                        {t('app.shortName')}
                    </Typography>
                    <Tooltip
                        title={
                            <Box>
                                <Typography variant="subtitle2">{t('app.fullName')}</Typography>
                                <Typography variant="caption" display="block">
                                    {t('header.version')}: {appVersion}
                                </Typography>
                                <Typography variant="caption" display="block">
                                    {t('header.lastProgramUpdate')}: {lastProgramUpdate}
                                </Typography>
                                <Typography variant="caption" display="block">
                                    {t('header.databaseLastRefresh')}:{' '}
                                    {refreshData?.lastRefresh ?? '-'}
                                </Typography>
                            </Box>
                        }
                    >
                        <IconButton
                            aria-label={t('header.applicationInformation')}
                            size="small"
                            sx={{ ml: 0.25 }}
                        >
                            <InfoIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {appVersion && (
                        <Chip
                            label={`v${appVersion}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.65rem', ml: 0.5, ...WIDE_ONLY }}
                        />
                    )}
                    <HeaderPageSearch />
                </Box>

                <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{ ml: 'auto', minWidth: 0, overflow: 'hidden' }}
                >
                    <BridgeStatusIndicator onCallSettled={handleBridgeCallSettled} />
                    {/* On a narrow window the figures wrap onto a second line rather than being
                        cut off; each one stays whole. */}
                    <Box
                        data-testid="header-legacy-status"
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-end"
                        flexWrap="wrap"
                        columnGap={1.25}
                        sx={{ minWidth: 0, '& > *': { whiteSpace: 'nowrap' } }}
                    >
                        {isAdmin && (
                            <Tooltip
                                title={
                                    cashStandsTotal
                                        ? formatExactAmount(Number(cashStandsTotal.lei))
                                        : ''
                                }
                            >
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    data-testid="header-total-cash-safe"
                                >
                                    {t('header.totalCashSafe')}:{' '}
                                    {cashStandsTotal === null || cashStandsTotal === undefined
                                        ? '-'
                                        : formatAmount(Number(cashStandsTotal.lei))}
                                </Typography>
                            </Tooltip>
                        )}
                        {isAdmin && (
                            <Tooltip
                                title={
                                    cumulativeDifference
                                        ? t('header.cashDifferenceBreakdown', {
                                              since: cumulativeDifference.since,
                                              handovers: formatExactAmount(
                                                  Number(cumulativeDifference.handoverDifference),
                                              ),
                                              registry: formatExactAmount(
                                                  Number(cumulativeDifference.registryAdjustment),
                                              ),
                                          })
                                        : ''
                                }
                            >
                                <Typography
                                    variant="caption"
                                    color={
                                        headerDifference !== null && headerDifference < 0
                                            ? 'error.main'
                                            : 'text.secondary'
                                    }
                                    data-testid="header-cash-difference"
                                >
                                    {t('header.cashDifference')}:{' '}
                                    {headerDifference === null
                                        ? '-'
                                        : formatAmount(headerDifference)}
                                </Typography>
                            </Tooltip>
                        )}
                        <Tooltip
                            title={`${t('header.cashDrawerStanding')}${
                                cashDrawerBalance === null
                                    ? ''
                                    : `: ${formatExactAmount(cashDrawerBalance)}`
                            }`}
                        >
                            <Chip
                                icon={<WalletIcon sx={{ fontSize: 14 }} />}
                                label={`${t('header.cashSituation')}: ${cashDrawerBalance === null ? '-' : formatAmount(cashDrawerBalance)}`}
                                color="primary"
                                variant="outlined"
                                size="small"
                                onClick={() => navigate('/pred_casa')}
                                data-testid="header-cash-situation"
                                sx={{ cursor: 'pointer', height: 24, fontSize: '0.7rem' }}
                            />
                        </Tooltip>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={WIDE_ONLY}
                            data-testid="header-date"
                        >
                            {t('header.date')}: {currentTime.toLocaleDateString()}
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={WIDE_ONLY}
                            data-testid="header-time"
                        >
                            {t('header.time')}: {currentTime.toLocaleTimeString()}
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            data-testid="header-bnr-rate"
                        >
                            {t('header.bnrRate')}:{' '}
                            {grandTotals?.exchangeRate === null ||
                            grandTotals?.exchangeRate === undefined
                                ? '-'
                                : formatExchangeRate(grandTotals.exchangeRate)}
                        </Typography>
                    </Box>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap', ...WIDE_ONLY }}
                        data-testid="header-user-label"
                    >
                        {t('header.user')}: {user?.username}
                    </Typography>

                    {isAdmin && pendingApprovalsCount > 0 && (
                        <Tooltip
                            title={t('header.pendingApprovals', {
                                count: pendingApprovalsCount,
                                defaultValue: `Pending approvals: ${pendingApprovalsCount}`,
                            })}
                        >
                            <IconButton
                                color="warning"
                                onClick={() => navigate('/control-room')}
                                size="small"
                                sx={{ ml: 1 }}
                            >
                                <Badge badgeContent={pendingApprovalsCount} color="error">
                                    <NotificationsIcon fontSize="small" />
                                </Badge>
                            </IconButton>
                        </Tooltip>
                    )}

                    {isAdmin && <EInvoiceDriftBadge />}

                    <Box
                        display="flex"
                        alignItems="center"
                        sx={{
                            cursor: 'pointer',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            },
                        }}
                        data-testid="header-user-menu"
                        onClick={handleMenuOpen}
                    >
                        <Avatar
                            sx={{
                                backgroundColor: 'primary.main',
                                width: 24,
                                height: 24,
                                mr: 0.75,
                                fontSize: '0.75rem',
                            }}
                        >
                            <UserIcon sx={{ fontSize: 14 }} />
                        </Avatar>
                        <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                            {user?.username}
                        </Typography>
                    </Box>

                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <MenuItem onClick={() => handleMenuClick('profile')} dense>
                            <ListItemIcon>
                                <UserIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>{t('header.profile')}</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => handleMenuClick('change-password')} dense>
                            <ListItemIcon>
                                <KeyIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>{t('header.changePassword')}</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => handleMenuClick('settings')} dense>
                            <ListItemIcon>
                                <SettingsIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>{t('header.settings')}</ListItemText>
                        </MenuItem>
                        {isAdmin && (
                            <MenuItem onClick={() => handleMenuClick('debug')} dense>
                                <ListItemIcon>
                                    <BugIcon fontSize="small" color="warning" />
                                </ListItemIcon>
                                <ListItemText>{t('header.debugWindow')}</ListItemText>
                            </MenuItem>
                        )}
                        <MenuItem onClick={() => handleMenuClick('help')} dense>
                            <ListItemIcon>
                                <HelpIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText>{t('header.help')}</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => handleMenuClick('changelog')} dense>
                            <ListItemIcon>
                                <ChangelogIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText>{t('header.changelog')}</ListItemText>
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={() => handleMenuClick('logout')} dense>
                            <ListItemIcon>
                                <LogoutIcon fontSize="small" color="error" />
                            </ListItemIcon>
                            <ListItemText sx={{ color: 'error.main' }}>
                                {t('header.logout')}
                            </ListItemText>
                        </MenuItem>
                    </Menu>
                </Box>
            </Toolbar>
        </AppBar>
    );
};

export default Header;
